import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import AiService from '../services/aiService';
import { generateConversationTitle, scrollToBottom } from '../utils/markdownUtils';

// Custom hook to manage chat state and logic
export const useChat = (apiBaseUrl, currentUserId) => {
  // State management
  const [activeTab, setActiveTab] = useState("aiChatIntro");
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [aiProvider, setAiProvider] = useState('ollama');
  const [streamingEnabled, setStreamingEnabled] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesRef = useRef(null);
  
  // Initialize AI service with useMemo to prevent recreation on every render
  const aiService = useMemo(() => new AiService(apiBaseUrl, aiProvider), [apiBaseUrl, aiProvider]);
  
  // Update AI service provider when it changes
  useEffect(() => {
    aiService.setProvider(aiProvider);
  }, [aiProvider, aiService]);

  // Load a specific conversation
  const loadConversation = useCallback(async (convId) => {
    try {
      setConversationId(convId);
      setMessages([{ type: 'typing' }]); // Show loading indicator
      
      const loadedMessages = await aiService.loadConversationMessages(convId);
      setMessages(loadedMessages);
      
      scrollToBottom(messagesRef, 100);
      return true;
    } catch (error) {
      console.error('Error loading conversation:', error);
      setMessages([{
        type: 'ai',
        text: 'Sorry, I couldn\'t load this conversation. Please try again.'
      }]);
      return false;
    }
  }, [aiService]);

  // Initialize chat - fetch conversations and load most recent if available
  const initializeChat = useCallback(async () => {
    try {
      if (!currentUserId) {
        return;
      }
      const fetchedConversations = await aiService.fetchConversations(currentUserId);
      setConversations(fetchedConversations);
      // If no conversations exist, don't create one yet - wait for user to send a message
      if (fetchedConversations.length === 0) {
        console.log('No existing conversations found');
      } else {
        console.log('Using existing conversations');
        // If we have conversations but no active conversation ID, load the most recent one
        if (!conversationId && fetchedConversations.length > 0) {
          const mostRecentConversation = fetchedConversations[0];
          console.log('Loading most recent conversation:', mostRecentConversation.id);
          await loadConversation(mostRecentConversation.id);
          setActiveTab('aiChatNew');
        }
      }
    } catch (error) {
      console.error('Error initializing chat:', error);
    }
  }, [aiService, currentUserId, conversationId, loadConversation]);

  // Fetch conversations on mount
  useEffect(() => {
    initializeChat();
  }, [initializeChat]);

  // Create a new conversation
  const createNewConversation = async () => {
    try {
      const newConvId = await aiService.createConversation(currentUserId);
      setConversationId(newConvId);
      setMessages([]);
      await refreshConversations();
      return newConvId;
    } catch (error) {
      console.error('Error creating new conversation:', error);
      return null;
    }
  };

  // Refresh conversations list
  const refreshConversations = async () => {
    try {
      if (!currentUserId) return;
      const fetchedConversations = await aiService.fetchConversations(currentUserId);
      setConversations(fetchedConversations);
    } catch (error) {
      console.error('Error refreshing conversations:', error);
    }
  };

  // Handle streaming message
  const handleStreamingMessage = async (convId, input) => {
    setIsStreaming(true);
    
    // Add empty AI message that we'll update with streaming content
    let aiMessageIndex = -1;
    setMessages(prev => {
      const newMessages = prev.filter(msg => msg.type !== "typing").concat({
        type: "ai",
        text: "",
        streaming: true
      });
      aiMessageIndex = newMessages.length - 1;
      return newMessages;
    });
    
    const onChunk = (chunk) => {
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages[aiMessageIndex]) {
          newMessages[aiMessageIndex] = {
            ...newMessages[aiMessageIndex],
            text: newMessages[aiMessageIndex].text + chunk
          };
        }
        return newMessages;
      });
      
      // Auto-scroll during streaming
      scrollToBottom(messagesRef, 10);
    };
    
    const onComplete = () => {
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages[aiMessageIndex]) {
          newMessages[aiMessageIndex] = {
            ...newMessages[aiMessageIndex],
            streaming: false
          };
        }
        return newMessages;
      });
      setIsStreaming(false);
      console.log('Streaming completed');
    };
    
    const onError = (error) => {
      setMessages(prev =>
        prev.filter(msg => msg.type !== "typing").concat({
          type: "ai",
          text: `Sorry, I encountered an error during streaming: ${error.message}. Please try again.`
        })
      );
      setIsStreaming(false);
    };
    
    await aiService.sendStreamingMessage(convId, input, onChunk, onComplete, onError, currentUserId);
  };

  // Send a message
  const sendMessage = async (input) => {
    if (!input) return;

    // Add user message
    setMessages(prev => [
      ...prev,
      { type: "user", text: input },
      streamingEnabled ? null : { type: "typing" }
    ].filter(Boolean));
    
    scrollToBottom(messagesRef, 50);

    try {
      // Create a new conversation if we don't have one
      let currentConvId = conversationId;
      if (!currentConvId) {
        currentConvId = await createNewConversation();
        if (!currentConvId) {
          throw new Error('Failed to create conversation');
        }
      }
      
      // Use streaming or regular API based on user preference
      if (streamingEnabled) {
        await handleStreamingMessage(currentConvId, input);
      } else {
        const response = await aiService.sendMessage(currentConvId, input, currentUserId);
        
        // Update messages with AI response
        setMessages(prev =>
          prev.filter(msg => msg.type !== "typing").concat({
            type: "ai",
            text: response.message,
            thinkProcess: response.thinkProcess
          })
        );
        
        scrollToBottom(messagesRef, 50);
        
        // Refresh conversations if title was updated
        if (response.titleUpdated) {
          await refreshConversations();
        }
      }
      
      // Generate a title for the conversation if it's the first message
      if (messages.length === 0) {
        const title = generateConversationTitle(input);
        await updateConversationTitle(currentConvId, title);
        await refreshConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove typing indicator and show error
      setMessages(prev =>
        prev.filter(msg => msg.type !== "typing").concat({
          type: "ai",
          text: `Sorry, I encountered an error: ${error.message}. Please try again.`
        })
      );
      
      scrollToBottom(messagesRef, 100);
    }
  };

  // Update conversation title
  const updateConversationTitle = async (convId, title) => {
    try {
      await aiService.updateConversationTitle(convId, title);
      await refreshConversations();
    } catch (error) {
      console.error('Error updating conversation title:', error);
    }
  };

  // Delete a conversation
  const deleteConversation = async (convId) => {
    try {
      await aiService.deleteConversation(convId);
      
      // Remove the conversation from the local state
      setConversations(prev => prev.filter(conv => conv.id !== convId));
      
      // If the deleted conversation was the active one, reset the state
      if (conversationId === convId) {
        setConversationId(null);
        setMessages([]);
        setActiveTab('aiChatIntro');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Handle send message
  const handleSendMessage = () => {
    if (inputValue.trim() !== "") {
      sendMessage(inputValue);
      setInputValue("");
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  // Toggle tabs
  const toggleAiTab = (tab) => setActiveTab(tab);

  return {
    // State
    activeTab,
    messages,
    inputValue,
    conversationId,
    conversations,
    aiProvider,
    streamingEnabled,
    isStreaming,
    messagesRef,
    
    // Actions
    setAiProvider,
    setStreamingEnabled,
    handleInputChange,
    handleSendMessage,
    handleKeyPress,
    toggleAiTab,
    loadConversation,
    createNewConversation,
    updateConversationTitle,
    deleteConversation,
    refreshConversations
  };
};